'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface WorkflowStep {
  id: number;
  title: string;
  description: string;
  icon: string;
  metrics: string;
  color: string;
}

const WorkflowVisualizer = () => {
  const [activeStep, setActiveStep] = useState<number>(1);

  const workflowSteps: WorkflowStep[] = [
    {
      id: 1,
      title: 'Lead Capture',
      description: 'Intelligent forms and landing pages automatically capture and qualify leads across all channels with real-time data enrichment.',
      icon: 'UserPlusIcon',
      metrics: '+340% Lead Quality',
      color: 'bg-accent'
    },
    {
      id: 2,
      title: 'Segmentation',
      description: 'AI-powered behavioral analysis creates dynamic audience segments based on engagement patterns, demographics, and intent signals.',
      icon: 'FunnelIcon',
      metrics: '+156% Targeting Accuracy',
      color: 'bg-success'
    },
    {
      id: 3,
      title: 'Personalization',
      description: 'Machine learning algorithms generate personalized content, messaging, and offers tailored to each prospect\'s journey stage.',
      icon: 'SparklesIcon',
      metrics: '+289% Engagement Rate',
      color: 'bg-primary'
    },
    {
      id: 4,
      title: 'Multi-Channel Delivery',
      description: 'Orchestrated campaigns deploy across email, social, SMS, and web with optimal timing and frequency for each channel.',
      icon: 'PaperAirplaneIcon',
      metrics: '+178% Reach Efficiency',
      color: 'bg-accent'
    },
    {
      id: 5,
      title: 'Nurture Sequences',
      description: 'Automated drip campaigns adapt in real-time based on prospect behavior, moving leads through the funnel intelligently.',
      icon: 'ArrowPathIcon',
      metrics: '+234% Conversion Rate',
      color: 'bg-success'
    },
    {
      id: 6,
      title: 'Analytics & Optimization',
      description: 'Continuous performance monitoring with AI-driven insights automatically optimize campaigns for maximum ROI.',
      icon: 'ChartBarIcon',
      metrics: '+445% ROI Improvement',
      color: 'bg-primary'
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-muted px-4 py-2 rounded-full mb-4">
            <Icon name="CogIcon" size={20} className="text-primary" />
            <span className="text-sm font-semibold text-primary">Campaign Journey Mapping</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Your Marketing Automation Workflow
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            See how our AI-powered system transforms every stage of your marketing operations into a seamless, high-performing revenue engine.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-4">
            {workflowSteps.map((step, index) => (
              <div key={step.id}>
                <button
                  onClick={() => setActiveStep(step.id)}
                  className={`w-full text-left p-6 rounded-xl transition-all duration-300 ${
                    activeStep === step.id
                      ? 'bg-card shadow-elevated border-2 border-primary'
                      : 'bg-card hover:bg-muted border-2 border-transparent'
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 ${step.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon name={step.icon as any} size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-heading font-bold text-foreground">
                          {index + 1}. {step.title}
                        </h3>
                        <span className="text-sm font-semibold text-success">{step.metrics}</span>
                      </div>
                      {activeStep === step.id && (
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>
                      )}
                    </div>
                    <Icon
                      name={activeStep === step.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                      size={20}
                      className="text-muted-foreground"
                    />
                  </div>
                </button>
                {index < workflowSteps.length - 1 && (
                  <div className="flex justify-center py-2">
                    <Icon name="ArrowDownIcon" size={24} className="text-muted-foreground" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="lg:sticky lg:top-24">
            <div className="bg-card rounded-2xl p-8 shadow-elevated border border-border">
              <div className="mb-6">
                <div className={`w-16 h-16 ${workflowSteps[activeStep - 1].color} rounded-xl flex items-center justify-center mb-4`}>
                  <Icon name={workflowSteps[activeStep - 1].icon as any} size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
                  {workflowSteps[activeStep - 1].title}
                </h3>
                <p className="text-base text-muted-foreground leading-relaxed mb-4">
                  {workflowSteps[activeStep - 1].description}
                </p>
                <div className="inline-flex items-center space-x-2 bg-success/10 px-4 py-2 rounded-lg">
                  <Icon name="TrendingUpIcon" size={20} className="text-success" />
                  <span className="text-sm font-semibold text-success">
                    {workflowSteps[activeStep - 1].metrics}
                  </span>
                </div>
              </div>

              <div className="space-y-4 pt-6 border-t border-border">
                <h4 className="text-sm font-heading font-bold text-foreground uppercase tracking-wide">
                  Key Capabilities
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                    <p className="text-sm text-muted-foreground">Real-time data processing and analysis</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                    <p className="text-sm text-muted-foreground">Seamless integration with existing tools</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                    <p className="text-sm text-muted-foreground">Automated optimization and A/B testing</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                    <p className="text-sm text-muted-foreground">Comprehensive reporting and insights</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkflowVisualizer;